import { Component, computed, input, Input, output } from '@angular/core';
import { AgentStepModel, StepType } from 'src/app/models/api/agentStep.model';
import { Evidence } from 'src/app/models/api/finding.model';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { CommonModule } from '@angular/common';
import { AnchorLinkComponent } from 'src/app/components/anchor-button/anchor-link.component';
import { SensitiveMessageContentComponent } from '../activities-messages/sensitive-content/sensitive-message-content.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AnyResourceId,
  SensitiveDataSnippet,
  SensitiveDataToolInvocationParams,
  SensitiveDataToolInvocationResult,
  SensitiveDataType,
} from '../activities-messages/activity-message.types';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';

interface EvidenceModel {
  stepType: StepType | undefined;
  location: string;
  isSensitiveContent: boolean;
  coreEvidence: string;
  senstiveDataIndexes: string;
  sensitiveDataType: SensitiveDataSnippet | SensitiveDataToolInvocationParams | SensitiveDataToolInvocationResult;
}

@Component({
  selector: 'app-evidence',
  standalone: true,
  templateUrl: './evidence.component.html',
  styleUrls: ['./evidence.component.scss'],
  imports: [
    CommonModule,
    AnchorLinkComponent,
    BeautifyDisplayStringPipe,
    SensitiveMessageContentComponent,
    MatTooltipModule,
  ],
})
export class EvidenceComponent {
  public evidenceList = input.required<Evidence[]>();
  public relatedSteps = input.required<AgentStepModel[]>();
  public evidenceStepClicked = output<MouseEvent>();

  protected evidenceModels = computed(() =>
    this.evidenceList().reduce(
      (evidences, evidence) => {
        return {
          ...evidences,
          [evidence.stepId]: {
            stepType: this.getStepType(evidence),
            location: this.getLocation(evidence),
            isSensitiveContent: this.isSensitiveContent(evidence),
            coreEvidence: this.getCoreEvidence(evidence),
            senstiveDataIndexes: this._getAdditionalDataRecord(evidence)?.[this._sensitiveContentStartIndexesKey] ?? '',
            sensitiveDataType: this.getSensitiveDataType(evidence),
          },
        };
      },
      {} as Record<Evidence['stepId'], EvidenceModel>
    )
  );

  protected SensitiveDataType = SensitiveDataType;

  private readonly _locationSeparator = ' > ';
  private readonly _sensitiveSourceKey = 'sensitiveSource';
  private readonly _sensitiveContentStartIndexesKey = 'sensitiveContentStartIndexes';

  constructor(
    private readonly _beautifyDisplayStringPipe: BeautifyDisplayStringPipe,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _logger: LoggerService
  ) {}

  private getStepType(evidence: Evidence): StepType | undefined {
    const stepDetails = this.getStepDetailsFromEvidence(evidence);
    if (!stepDetails) {
      return undefined;
    }
    return stepDetails.type;
  }

  private getLocation(evidence: Evidence): string {
    let result = '';
    const splitLocation = evidence.location.split('.');
    if (splitLocation.length > 0) {
      const formattedString = this._beautifyDisplayStringPipe.transform(splitLocation[splitLocation.length - 1]);
      result += `${this._locationSeparator}${formattedString}`;
    }
    return result;
  }

  private isSensitiveContent(evidence: Evidence): boolean {
    const data = this._getAdditionalDataRecord(evidence);
    return (
      data?.[this._sensitiveContentStartIndexesKey] !== undefined || data?.[this._sensitiveSourceKey] !== undefined
    );
  }

  private getCoreEvidence(evidence: Evidence): string {
    // Find anything between < and >
    let coreEvidence = evidence.coreEvidence;
    const pattern = /<([^>]+)>/g;
    if (this.isSensitiveContent(evidence)) {
      if (!evidence.coreEvidence.toLowerCase().includes('fetch content')) {
        coreEvidence += ' ' + this._labelsProviderPipe.transform('evidenceListPage.sensitiveContentEvidence');
      }
    }
    return coreEvidence.replace(pattern, '<strong>$1</strong>');
  }

  private getStepDetailsFromEvidence(evidence: Evidence): AgentStepModel | undefined {
    return this.relatedSteps().find((step) => step.id === evidence.stepId);
  }

  private _getAdditionalDataRecord(evidence: Evidence): Record<string, string> | undefined {
    return typeof evidence.additionalData === 'string' ? undefined : evidence.additionalData;
  }

  private getSensitiveDataType(
    evidence: Evidence
  ): SensitiveDataSnippet | SensitiveDataToolInvocationParams | SensitiveDataToolInvocationResult {
    switch (this._getAdditionalDataRecord(evidence)?.[this._sensitiveSourceKey]) {
      case 'payload':
        return {
          type: SensitiveDataType.SNIPPET,
        } satisfies SensitiveDataSnippet;
      case 'invocationParams':
      case 'invocationResult':
        return {
          type: SensitiveDataType.TOOL_INVOCATION_PARAMS,
          resourceId: '***' as AnyResourceId,
        } satisfies SensitiveDataToolInvocationParams;

      default:
        this._logger.warn('Invalid sensitive data type');
        return {
          type: SensitiveDataType.SNIPPET,
        } satisfies SensitiveDataSnippet;
    }
  }
}
